<template lang="pug">
v-layout#answer(row wrap)
  v-flex(xs12)
    h2.display-1.text-xs-center.mt-5.mb-2 {{ theme.title }}

  v-flex(xs12)
    v-layout(row wrap)
      v-flex(xs12)
        timer.grey--text.text--darken-1(:value="timerOn" :seconds="theme.limited_seconds")
      v-layout
        v-flex(xs12)
          balloon-counter.balloon-counter(:value="counter")
          .form-container
            popup-text(v-for="(text, i) in inputEffectTexts" :key="`popup-${i}`" :value="text" color="red")
            ripples(v-for="(_, i) in inputEffectTexts" :key="`ripples-${i}`")
            v-form.mt-3(ref="form" @submit.prevent="")
              v-text-field.answer-field(
                ref="answer"
                v-validate="validates.answer"
                v-model="answerBody"
                :placeholder="$t('pages.user.answers.new.placeholder.text')"
                :error-messages="errors.collect('answer')"
                :counter="validates.answer.max"
                type="text"
                color="accent"
                name="answer"
                key="answer"
                single-line
                autocomplete="off"
                :disabled="times === 0"
                @keypress.enter="input")

  v-flex(xs12)
    // 前回回答
    template(v-if="value.prev_answer_items_count")
      .progress.mt-3
        .subheading
          span {{ `前回の入力数「${value.prev_answer_items_count}」を越えよう！` }}
        .counter
          .counter-text(v-if="counter < value.prev_answer_items_count")
            | {{ `${counter} / ${value.prev_answer_items_count}` }}
          .counter-text(v-else)
            reveal-text(value="達成しました！" font-size="1.2em" color="white")
          v-progress-linear(:value="prevCounterValue" color="primary" :height="45")

      // 最大回答 (前回回答 == 最大回答の場合は表示しない)
      .progress.mt-3(v-if="value.answer_items_count_maximum > value.prev_answer_items_count")
        .subheading
          span {{ `自身の最大入力数「${value.answer_items_count_maximum}」を越えよう！` }}
        .counter
          .counter-text(v-if="counter < value.answer_items_count_maximum")
            | {{ `${counter} / ${value.answer_items_count_maximum}` }}
          .counter-text(v-else)
            reveal-text(value="達成しました！" font-size="1.2em" color="white")
          v-progress-linear(:value="maximumCounterValue" color="primary" :height="45")

    // 初回回答
    .progress.mt-3(v-else)
      .subheading
        span {{ `目標入力数「${FIRST_TARGET_INPUT_COUNT}」を目指そう！` }}
      .counter
        .counter-text(v-if="counter < FIRST_TARGET_INPUT_COUNT")
          | {{ `${counter} / ${FIRST_TARGET_INPUT_COUNT}` }}
        .counter-text(v-else)
          reveal-text(value="達成しました！" font-size="1.2em" color="white")
        v-progress-linear(:value="maximumCounterValue" color="primary" :height="45")

  dialog-confirm(
    v-model="dialogConfirmController.active"
    :text-body="dialogConfirmController.body"
    :text-close="$t('text.cancel')"
    :text-agree="textAgree"
    html-mode
    @close="dialogConfirmController.doCancel()"
    @agree="dialogConfirmController.doAgree()")
  countdown-overlay(v-if="overlay" :message="startMessage" @finish="answerStart")
</template>

<script>
'use strict'
import Vue from 'vue'
import { Validator } from 'vee-validate'
import Theme from '@/models/theme'
import BalloonCounter from '@/components/BalloonCounter'
import CountdownOverlay from '@/components/CountdownOverlay'
import DialogConfirm from '@/components/dialog/DialogConfirm'
import RevealText from '@/components/text/RevealText'
import Timer from '@/components/text/Timer'
import PopupText from '@/components/text/PopupText'
import Ripples from '@/components/Ripples'
import DialogConfirmController from '@/controllers/dialog-confirm-controller'
import { WordChecker } from '@/utils/word-checker'

const FIRST_TARGET_INPUT_COUNT = 10 // 最初の入力数目標値

export default {
  components: {
    CountdownOverlay,
    BalloonCounter,
    DialogConfirm,
    PopupText,
    RevealText,
    Ripples,
    Timer
  },
  props: {
    value: Object,
    backRoute: Object
  },
  data () {
    return {
      startMessage: "",
      theme: new Theme(),
      answerBody: "",
      answerItems: [],
      overlay: false,
      times: 0,
      progressTimes: 0,
      timerOn: false,
      dialogConfirmController: new DialogConfirmController(),
      textAgree: this.$t('text.ok'),
      validates: {
        answer: {
          max: FIRST_TARGET_INPUT_COUNT,
          uniqueness: true,
          correctWord: true
        }
      },
      inputEffectTexts: []
    }
  },
  computed: {
    FIRST_TARGET_INPUT_COUNT () {
      return FIRST_TARGET_INPUT_COUNT
    },
    counter () {
      return this.answerItems.length
    },
    calcTimeProgress () {
      if (this.progressTimes <= 0)
        return 100
      const step = 100.0 / this.theme.limited_seconds
      const time = step * (this.theme.limited_seconds - this.progressTimes)
      return time
    },
    postParams () {
      return {
        answer: {
          answer_items_attributes: this.answerItems.map((e) => {
            return { body: e }
          })
        }
      }
    },
    prevCounterValue () {
      const cnt = Math.ceil(100.0 / this.value.prev_answer_items_count)
      return Math.min(...[100, cnt * this.counter])
    },
    maximumCounterValue () {
      if (this.value.prev_answer_items_count) {
        const cnt = Math.ceil(100.0 / this.value.answer_items_count_maximum)
        return Math.min(...[100, cnt * this.counter])
      } else {
        const cnt = Math.ceil(100.0 / FIRST_TARGET_INPUT_COUNT)
        return Math.min(...[100, cnt * this.counter])
      }
    }
  },
  timers: {
    countdown: {
      time: 1000,
      autostart: false,
      repeat: true,
      immediate: false,
    },
    submit: {
      time: 1000,
      autostart: false,
      repeat: false,
      immediate: false,
    }
  },
  watch: {
    theme (value) {
      if (process.env.NODE_ENV === 'development' && process.env.ANSWER_LIMITED_SECONDS)
        value.limited_seconds = Number(process.env.ANSWER_LIMITED_SECONDS)
      this.times = value.limited_seconds
      this.progressTimes = value.limited_seconds
      this.$store.commit('setBackRoute', this.backRoute)
      this.startMessage = `「${value.title}」${this.$t('pages.user.answers.new.startMessageSuffix')}`
    }
  },
  beforeRouteLeave (to, from, next) {
    this.$timer.stop('countdown')
    this.$timer.stop('submit')
    next()
  },
  mounted () {
    document.body.onpaste = () => false

    Validator.extend('uniqueness', {
      getMessage: () => '同じワードを複数回入力はできません',
      validate: (value) => {
        return !this.answerItems.includes(value)
      }
    })

    Validator.extend('correctWord', {
      getMessage: () => '意味のある言葉を入力してください',
      validate: (value) => {
        if (!value) {
          return true
        }

        const wordChecker = new WordChecker(value)

        // 数字だけは不可
        if (wordChecker.isNnum()) {
          return false
        }

        // 1文字入力を許すのは漢字だけ
        if (value.length === 1 && !wordChecker.isKanji()) {
          return false
        }

        // 同文字連打か
        if (wordChecker.isConsecutive()) {
          return false
        }

        return true
      }
    })
  },
  methods: {
    start () {
      this.theme = this.value.theme
      if (this.value.isAnswered)
        this.dialogConfirmController.open(
          this.$t('pages.user.answers.new.confirmOverwritingMessage'),
          () => (this.overwritingAnswerStart()),
          () => (this.cancel()))
      else
        this.overlay = true
    },
    reject (message) {
      this.textAgree = this.$t('text.retry')
      this.dialogConfirmController.open(
        message,
        () => (this.retry()),
        () => (this.cancel()))
    },
    overwritingAnswerStart () {
      this.dialogConfirmController.close()
      this.overlay = true
    },
    answerStart () {
      this.$refs.answer.$el.querySelector('input').focus()
      this.progressTimes--
      this.$timer.start('countdown')
      this.timerOn = true
    },
    countdown () {
      this.times--
      this.progressTimes--
      if (this.times <= 0) {
        this.$timer.stop('countdown')
        this.finish()
      }
    },
    submit () {
      this.$emit('on-submit', this.postParams)
    },
    input () {
      this.$validator.validate().then(result => {
        if (!result || this.answerBody.length === 0) {
          return
        }

        if (this.value.inputted_words_in_past.length === 0 || this.value.inputted_words_in_past.includes(this.answerBody)) {
          this.inputEffectTexts.push('')
        } else {
          this.inputEffectTexts.push('New！！！')
        }
        this.answerItems.push(this.answerBody)
        this.answerBody = ""
      })
    },
    finish () {
      if (this.counter === 0) {
        this.textAgree = this.$t('text.retry')
        this.dialogConfirmController.open(
          this.$t('pages.user.answers.new.confirmRetryMessage'),
          () => (this.retry()),
          () => (this.cancel()))
      }
      this.$timer.start('submit')
    },
    retry () {
      window.location.reload()
    },
    cancel () {
      this.$store.dispatch('back')
    }
  }
}
</script>

<style lang="styl" scoped>
.balloon-counter
  float: left
  top -30px
  left -10px

@media screen and (max-width 768px)
  .balloon-counter
    transform: scale(.7, .7)
    top -20px
    left 0px

>>>
  .v-form
    height 90px

  .v-progress-circular__overlay
    transition all 1.0s ease-in-out

  .timer
    float: left
    top -30px
    left -20px

  .counter
    width 100%
    position relative

  .counter-text
    font-size 1.5em
    position absolute
    right 10px
    bottom 5px
    z-index 3

  .reveal-text 
    position absolute
    right 3px
    bottom -3px
    z-index 3

  .form-container
    position relative

  .popup-text-container
    top 12px

  .ripples-container
    top 30px
    left -30%

  @media screen and (max-width 768px)
    .ripples-container
      top 30px
      left -25%
</style>
